/**
 * loginLinkViewModel — `(state, failed, pendingSwitch)` → les props de `<LoginLinkPage>`.
 *
 * Deux sources. Le compte ouvert sur l'appareil (le store : son nom, et le rang de la session —
 * `sessionRank`, la règle de `/login`). L'échange du lien (le crochet de l'écran) : `failed` (pas de
 * jeton, ou le serveur l'a refusé) et `pendingSwitch`, la réponse d'un lien qui appartient à un AUTRE
 * compte que celui qui est ouvert, gardée là tant que la personne n'a pas choisi.
 *
 * L'ordre des états est celui de `src/auth/LoginLinkScreen.js` : la question d'abord, puis « Ce lien
 * a expiré » — sauf si une session est déjà ouverte ici, où l'on y entre sans un mot
 * (`landsOnSession`) —, puis l'attente.
 *
 * Mémoïsé sur les chaînes du nom et le rang, pas sur `currentUser.object` (même raison que
 * `parentShellViewModel`).
 */
import { createSelector } from "reselect"

import { SESSION_RANK, sessionRank } from "../../../../../data/auth/crossTabSession"
import { accountDisplayName, type ApiLoginLinkSuccess } from "../../../adapters/auth/LoginLink/loginLinkAdapter"
import { type UILoginLinkStatus } from "../../../ui/types"
import { selectAccount } from "../../shared/displayedStudent"

export type LoginLinkViewModel = {
    status: UILoginLinkStatus
    currentUserName: string
    linkUserName: string
}

// Le store est en JS non typé : on lit les champs qu'on connaît, sans le décrire en entier.
/* eslint-disable-next-line @typescript-eslint/no-explicit-any */
type ReduxState = any

/** 0 déconnecté, 1 des comptes à choisir, 2 un compte ouvert. */
export const selectSessionRank = (state: ReduxState): number => sessionRank(state?.data)

/** Un lien refusé (ou sans jeton) là où une session existe déjà : on y entre, comme `/login`. */
export function landsOnSession(failed: boolean, rank: number): boolean {
    return failed && rank !== SESSION_RANK.NONE
}

export function loginLinkStatus(failed: boolean, rank: number, switchPending: boolean): UILoginLinkStatus {
    if (switchPending) return "switchAccount"
    if (failed && !landsOnSession(failed, rank)) return "expired"
    return "connecting"
}

const selectFirstname = (state: ReduxState): string | null => selectAccount(state)?.firstname ?? null
const selectLastname = (state: ReduxState): string | null => selectAccount(state)?.lastname ?? null
const selectEmail = (state: ReduxState): string | null => selectAccount(state)?.email ?? null
const selectFailed = (_state: ReduxState, failed: boolean): boolean => failed
const selectPendingSwitch = (
    _state: ReduxState,
    _failed: boolean,
    pendingSwitch: ApiLoginLinkSuccess | null
): ApiLoginLinkSuccess | null => pendingSwitch

export const loginLinkViewModel = createSelector(
    [selectFirstname, selectLastname, selectEmail, selectSessionRank, selectFailed, selectPendingSwitch],
    (firstname, lastname, email, rank, failed, pendingSwitch): LoginLinkViewModel => ({
        status: loginLinkStatus(failed, rank, pendingSwitch != null),
        currentUserName: accountDisplayName({ firstname, lastname, email }),
        linkUserName: pendingSwitch ? accountDisplayName(pendingSwitch.user) : "",
    })
)
